import { useLayoutEffect, useState } from 'react';
import { Optional } from '@/generic/functional';
import {
  type ApiInformation,
  type ApiInformationToDiagnose,
  createApiInformation,
  isApiInformationToDiagnose,
} from '@/diagnostic/domain/ApiInformation.ts';
import { useTranslation } from 'react-i18next';
import { SelectedRuleIds } from '@/diagnostic/domain/SelectedRuleIds.ts';
import type { DiagnosticCompleted } from '@/diagnostic/domain/Diagnostic.ts';
import { useDiagnosticTracking } from '@/diagnostic/primary/DiagnosticTrackingContext.ts';
import {
  IpponBadge,
  IpponButton,
  IpponCard,
  IpponGrid,
  IpponGridSlot,
  IpponImportFile,
  IpponTitle,
  IpponVSpace,
  IpponHSpace,
  IpponVSpaceSlot,
} from '@ippon-ui/react';
import { RulesSelectionComponent } from '@/diagnostic/primary/RulesSelectionComponent.tsx';
import { DiagnosticHistoryComponent } from '@/diagnostic/primary/DiagnosticHistoryComponent.tsx';
import { DiagnosticLoadingModalComponent } from '@/diagnostic/primary/DiagnosticLoadingModalComponent.tsx';
import { DiagnosticReportModalComponent } from '@/diagnostic/primary/DiagnosticReportModalComponent.tsx';
import { useOptional } from '@/generic/react-tools';

const ImportedApiFileComponent = ({
  apiInformation,
  onRemove,
}: {
  apiInformation: ApiInformation;
  onRemove: () => void;
}) => (
  <IpponHSpace gap={8}>
    <IpponBadge
      color="neutral"
      variant="secondary"
      dataSelector="diagnostic.import-api.file-name"
      iconRight={{
        name: 'close',
        variant: 'outline',
        onClick: onRemove,
        dataSelector: 'diagnostic.import-api.file-name.remove',
      }}
    >
      {apiInformation.content.name}
    </IpponBadge>
  </IpponHSpace>
);

const ImportApiFileComponent = ({
  optionalApiInformation,
  onImport,
  onRemove,
}: {
  optionalApiInformation: Optional<ApiInformation>;
  onImport: (file: File) => void;
  onRemove: () => void;
}) => {
  const { t } = useTranslation('diagnostic');

  return useOptional({
    optional: optionalApiInformation,
    onValuated: (apiInformation) => (
      <ImportedApiFileComponent apiInformation={apiInformation} onRemove={onRemove} />
    ),
    onEmpty: () => (
      <IpponImportFile
        title={t('diagnostic.import-file.title')}
        description={t('diagnostic.import-file.description')}
        dataSelector="diagnostic.import-api"
        onChange={onImport}
      />
    ),
  });
};

const OptionalDiagnosticReportModalComponent = ({
  optionalDiagnosticCompleted,
  open,
  onClose,
}: {
  optionalDiagnosticCompleted: Optional<DiagnosticCompleted>;
  open: boolean;
  onClose: () => void;
}) =>
  useOptional({
    optional: optionalDiagnosticCompleted,
    onValuated: (diagnostic) => (
      <DiagnosticReportModalComponent open={open} onClose={onClose} report={diagnostic.report} />
    ),
  });

export const DiagnoseComponent = () => {
  const tracking = useDiagnosticTracking();
  const { t } = useTranslation('diagnostic');
  const [apiInformation, setApiInformation] = useState<Optional<ApiInformation>>(Optional.empty());

  const { notifyPageMounted } = tracking;
  // Layout effect so the unmount cleanup runs synchronously in the same commit as the route
  // change, before any pending fetch (e.g. the report load after a background diagnostic
  // completes) can read a stale "mounted" ref through a passive effect's deferred cleanup.
  useLayoutEffect(() => {
    notifyPageMounted(true);
    return () => notifyPageMounted(false);
  }, [notifyPageMounted]);

  const setOfApiInformation = (apiInformation: ApiInformation) =>
    setApiInformation(Optional.of(apiInformation));

  const apiInformationToDiagnose = apiInformation.filter<ApiInformationToDiagnose>(
    isApiInformationToDiagnose,
  );

  const diagnose = () => apiInformationToDiagnose.ifPresent(tracking.start);

  const importApi = (file: File) => {
    setOfApiInformation(createApiInformation(file));
  };

  const removeApi = () => {
    setApiInformation(Optional.empty());
  };

  const selectRules = (selected: SelectedRuleIds) =>
    setApiInformation((current) => current.map((info) => info.withRules(selected)));

  return (
    <IpponGrid media={[4, 'desktop-m-12']} gap={24}>
      <IpponGridSlot col={[4, 'desktop-m-8']}>
        <IpponVSpace gap={24}>
          <IpponCard size="large" shadow="l3">
            <IpponVSpace gap={16}>
              <IpponTitle tag="h2" level={5} dataSelector="diagnostic.title">
                {t('diagnostic.import-your-file')}
              </IpponTitle>
              <ImportApiFileComponent
                optionalApiInformation={apiInformation}
                onImport={importApi}
                onRemove={removeApi}
              />
            </IpponVSpace>
          </IpponCard>
          {apiInformation.isPresent() ? <RulesSelectionComponent onSelected={selectRules} /> : null}
          {apiInformationToDiagnose.isPresent() || tracking.isLoading ? (
            <IpponVSpaceSlot align="left">
              {apiInformationToDiagnose.isPresent() ? (
                <IpponButton
                  size="large"
                  onClick={diagnose}
                  disabled={tracking.isLoading}
                  iconRight={{ name: 'paper-plane' }}
                  dataSelector="diagnostic.diagnose"
                >
                  {t('diagnostic.diagnose')}
                </IpponButton>
              ) : null}
              <DiagnosticLoadingModalComponent
                open={tracking.isLoading && tracking.modalVisible}
                progress={tracking.progress}
                error={tracking.failure}
                onRetry={tracking.retry}
                onClose={tracking.stop}
                onContinueInBackground={tracking.continueInBackground}
              />
            </IpponVSpaceSlot>
          ) : null}
        </IpponVSpace>
      </IpponGridSlot>
      <IpponGridSlot col={[4, 'desktop-m-4']}>
        <DiagnosticHistoryComponent />
      </IpponGridSlot>
      <OptionalDiagnosticReportModalComponent
        optionalDiagnosticCompleted={tracking.diagnosticCompleted}
        open={tracking.reportOpen}
        onClose={tracking.closeReport}
      />
    </IpponGrid>
  );
};
